import {CheckOutlined} from '@ant-design/icons';
import {List, Typography} from 'antd';
import parse, {domToReact} from 'html-react-parser';
import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';

import {getTagInfo} from '../../../consts/notifications/notifications.js';

const NotificationItem = ({item, onMarkAsRead}) => {
    const {name: tagName, color} = getTagInfo(item.typeCode);
    const isRead = item.isRead;

    const {timeString, dateString} = useMemo(() => {
        const safeDateString = item.createdAt?.endsWith('Z') ? item.createdAt : `${item.createdAt}Z`;
        const dateObj = new Date(safeDateString);

        return {
            timeString: dateObj.toLocaleTimeString('uk-UA', {hour: '2-digit', minute: '2-digit'}),
            dateString: dateObj.toLocaleDateString('uk-UA', {day: '2-digit', month: 'short'}),
        };
    }, [item.createdAt]);

    const parseOptions = {
        replace: (domNode) => {
            if (domNode.name === 'a' && domNode.attribs && domNode.attribs.href) {
                return (
                    <Link
                        to={domNode.attribs.href}
                        className='
                            !text-zambezi
                            !underline !decoration-[color:var(--item-color)] underline-offset-[3px]
                            transition-colors duration-200 ease-in-out
                            hover:!text-[var(--item-color)]
                        '
                        onClick={(e) => e.stopPropagation()}
                    >
                        {domToReact(domNode.children)}
                    </Link>
                );
            }
        },
    };

    return (
        <List.Item
            key={item.id}
            style={{
                '--item-color': color,
                '--item-hover-bg': `${color}08`,
                '--item-hover-border': `${color}25`,
            }}
            className={`
                group relative mb-3 overflow-hidden
                border border-solid rounded-[10px] p-4
                transition-all duration-300
                ${
                    isRead
                        ? 'bg-ghost-white border-whisper opacity-60 grayscale-[15%] shadow-none'
                        : 'bg-white border-whisper shadow-[0_4px_12px_-4px_rgba(0,0,0,0.04)] hover:bg-[var(--item-hover-bg)] hover:border-[var(--item-hover-border)]'
                }
            `}
        >
            <div className='flex items-stretch gap-3.5 w-full'>
                <div
                    className={`
                        flex-shrink-0 w-1 rounded-full my-0.5 transition-colors duration-300
                        ${isRead ? 'bg-whisper' : 'bg-[var(--item-color)]'}
                    `}
                />

                <div className='flex flex-col w-full py-0.5'>
                    <div className='flex justify-between items-start mb-1.5'>
                        <span
                            className={`
                                text-10 font-bold tracking-05 uppercase mt-0.5
                                ${isRead ? 'text-nobel' : 'text-[var(--item-color)]'}
                            `}
                        >
                            {tagName || 'Сповіщення'}
                        </span>

                        {!isRead && (
                            <div
                                onClick={() => onMarkAsRead(item.id)}
                                title='Позначити як прочитане'
                                className='
                                    w-6 h-6 flex items-center justify-center
                                    rounded-full transition-all duration-200 cursor-pointer
                                    text-nobel mt-[-4px] mr-[-4px]
                                    hover:bg-solitude hover:text-[var(--item-color)]
                                '
                            >
                                <CheckOutlined style={{fontSize: '11px', strokeWidth: '40px'}} />
                            </div>
                        )}
                    </div>

                    <div className='mb-3 pr-2'>
                        <Typography.Text
                            className={`
                                block text-14 font-semibold leading-13 mb-1.5
                                ${isRead ? 'text-charcoal' : 'text-midnight'}
                            `}
                        >
                            {item.title}
                        </Typography.Text>

                        <Typography.Text
                            className={`
                                block text-12 leading-15 whitespace-pre-line
                                ${isRead ? 'text-nobel' : 'text-zambezi'}
                            `}
                        >
                            {item.body ? parse(item.body, parseOptions) : ''}
                        </Typography.Text>
                    </div>

                    <div className='flex items-center gap-2 mt-auto'>
                        <span className='text-11 font-medium text-nobel'>{timeString}</span>
                        <span className='w-1 h-1 rounded-full bg-whisper' />
                        <span className='text-11 font-medium text-nobel'>{dateString}</span>
                    </div>
                </div>
            </div>
        </List.Item>
    );
};

export default React.memo(NotificationItem, (prevProps, nextProps) => {
    return prevProps.item.id === nextProps.item.id && prevProps.item.isRead === nextProps.item.isRead;
});
